import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, Mail, MapPin, MessageCircle, Plus } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { MaskedLine, Reveal, SectionHeading } from "@/components/Reveal";
import { waLink } from "@/lib/whatsapp";
import { WHATSAPP_NUMBER } from "@/lib/whatsapp";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

const WhatsAppCard = () => {
  const { t } = useLang();
  const c = t.contact;
  return (
    <div
      className="flex h-full flex-col justify-between rounded-sm bg-espresso p-8 text-offwhite sm:p-10"
      data-testid="contact-whatsapp-card"
    >
      <div>
        <span className="grid h-12 w-12 place-items-center rounded-full bg-teak/20">
          <MessageCircle className="h-6 w-6 text-terracotta" />
        </span>
        <h2 className="mt-8 font-display text-3xl font-light sm:text-4xl">{c.wa.title}</h2>
        <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-offwhite/65">
          {c.wa.sub}
        </p>
      </div>
      <div className="mt-10">
        <p className="eyebrow text-offwhite/50">{c.wa.numberLabel}</p>
        <p className="mt-2 font-display text-2xl font-light tracking-wide" data-testid="contact-whatsapp-number">
          +{WHATSAPP_NUMBER}
        </p>
        <a
          href={waLink(t.nav.waMessage)}
          target="_blank"
          rel="noreferrer"
          data-testid="contact-whatsapp-button"
          className="mt-6 inline-flex items-center gap-3 rounded-full bg-teak px-8 py-4 text-sm font-medium text-offwhite transition-all duration-300 hover:-translate-y-0.5 hover:bg-terracotta"
        >
          <MessageCircle className="h-5 w-5" />
          {c.wa.button}
        </a>
      </div>
    </div>
  );
};

const StudioInfo = () => {
  const { t } = useLang();
  const s = t.contact.studio;
  const rows = [
    { icon: MapPin, label: s.locationLabel, value: s.location },
    { icon: Mail, label: s.emailLabel, value: s.email },
    { icon: Clock, label: s.hoursLabel, value: s.hours },
  ];
  return (
    <div className="mt-6 rounded-sm border border-sand bg-beige/50 p-8 sm:p-10" data-testid="contact-studio-info">
      <p className="eyebrow">{s.title}</p>
      <ul className="mt-6 space-y-5">
        {rows.map((r) => (
          <li key={r.label} className="flex items-start gap-4">
            <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full border border-teak/30 text-teak">
              <r.icon className="h-4 w-4" />
            </span>
            <div>
              <p className="font-grotesk text-[11px] uppercase tracking-[0.22em] text-taupe">
                {r.label}
              </p>
              <p className="mt-1 text-sm font-light text-charcoal/85">{r.value}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Composer = () => {
  const { t } = useLang();
  const c = t.contact.composer;
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");

  const toggle = (name) =>
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name],
    );

  const composed = () =>
    [
      c.intro,
      selected.length ? `${c.serviceLabel}: ${selected.join(", ")}` : "",
      message,
    ]
      .filter(Boolean)
      .join("\n");

  return (
    <div className="rounded-sm border border-sand bg-offwhite p-8 sm:p-10" data-testid="whatsapp-composer">
      <h2 className="font-display text-3xl font-light text-espresso sm:text-4xl">{c.title}</h2>
      <p className="mt-3 text-sm font-light leading-relaxed text-charcoal/65">{c.sub}</p>

      <p className="eyebrow mt-8">{c.serviceLabel}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {t.services.map((s) => {
          const active = selected.includes(s.name);
          return (
            <button
              key={s.id}
              onClick={() => toggle(s.name)}
              data-testid={`composer-chip-${s.id}`}
              className={`rounded-full border px-4 py-2 text-xs transition-all duration-300 sm:text-sm ${
                active
                  ? "border-teak bg-teak text-offwhite"
                  : "border-sand text-taupe hover:border-teak hover:text-teak"
              }`}
            >
              {s.name}
            </button>
          );
        })}
      </div>

      <label className="eyebrow mt-8 block" htmlFor="composer-message">
        {c.messageLabel}
      </label>
      <textarea
        id="composer-message"
        data-testid="whatsapp-composer-textarea"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={c.placeholder}
        rows={4}
        className="mt-4 w-full resize-none rounded-sm border border-sand bg-offwhite p-4 text-sm font-light text-charcoal outline-none transition-colors placeholder:text-taupe/60 focus:border-teak"
      />

      <a
        href={waLink(composed())}
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-composer-submit-button"
        className="btn-dark mt-6 w-full"
      >
        <MessageCircle className="h-4 w-4" />
        {c.button}
      </a>
    </div>
  );
};

const Faq = () => {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="py-20 sm:py-24" data-testid="contact-faq">
      <div className="wrap grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <SectionHeading eyebrow={t.contact.faq.eyebrow} title={t.contact.faq.title} />
        </div>
        <div className="lg:col-span-8">
          <div className="border-t border-sand">
            {t.contact.faq.items.map((f, i) => {
              const open = openIdx === i;
              return (
                <div key={i} className="border-b border-sand" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    className="group flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <span
                      className={`font-display text-xl font-light transition-colors duration-300 sm:text-2xl ${
                        open ? "text-teak" : "text-espresso group-hover:text-teak"
                      }`}
                    >
                      {f.q}
                    </span>
                    <Plus
                      className={`h-5 w-5 shrink-0 text-espresso transition-transform duration-300 ${
                        open ? "rotate-45 text-teak" : ""
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="max-w-2xl pb-6 text-sm font-light leading-relaxed text-charcoal/70">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;

  return (
    <>
      <section className="pt-36 sm:pt-44">
        <div className="wrap">
          <motion.p {...fade(0.1)} className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-teak" />
            {c.hero.eyebrow}
          </motion.p>
          <h1 className="mt-6 font-display text-[clamp(2.6rem,6vw,5.6rem)] font-light leading-[1.02] tracking-tight text-espresso">
            <MaskedLine delay={0.25}>{c.hero.title}</MaskedLine>
          </h1>
          <motion.p
            {...fade(0.6)}
            className="mt-6 max-w-xl text-sm font-light leading-relaxed text-charcoal/70 sm:text-base"
          >
            {c.hero.sub}
          </motion.p>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="contact-main">
        <div className="wrap grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal>
            <WhatsAppCard />
            <StudioInfo />
          </Reveal>
          <Reveal delay={0.1}>
            <Composer />
          </Reveal>
        </div>
      </section>

      <Faq />
    </>
  );
}
